/**
 * ModifierDetails — «Постобработка» modifier overlay (TASK-189). Moved out of
 * ServiceManagementPage.tsx (KAN-157 god-component split).
 *
 * A leaf pricelist/classifier service can ALSO carry a chain modifier
 * (band-surcharge, TASK-187) — e.g. «итого = базовая_сумма * (1 + IF(...))».
 * That chain must NOT replace the price table / variants; it is rendered as an
 * OVERLAY block beneath the base body. This component renders ONLY the modifier
 * chain: its formula lines + input parameters.
 *
 * `базовая_сумма` is the implicit Σ of pricelist positions, not a parameter the
 * user types in — it is surfaced with an explanatory «сумма позиций» caption
 * rather than as a bare required-input line.
 */
import type { Chain, Service } from '@/services/api/services';
import { extractFormulaLines } from '@/utils/serviceFormula';

export function ModifierDetails({ service }: { service: Service }): JSX.Element | null {
  const chains = service.chains || [];
  if (chains.length === 0) return null;

  return (
    <div data-testid="modifier-postprocessing" className="space-y-3">
      <div>
        {/* Inner heading is «Надбавки и коэффициенты» (NOT «Постобработка»):
            the section's own DetailSection <h4> already carries «Постобработка»,
            so this avoids a duplicate «Постобработка» match inside the section
            (within(section).getByText(/Постобработка/i) would throw). */}
        <h5 className="text-sm font-semibold text-gray-700">Надбавки и коэффициенты</h5>
        <p className="text-xs text-gray-500">коэффициенты к базовой сумме позиций</p>
      </div>

      {chains.map((chain: Chain) => {
        const formulaLines = extractFormulaLines(chain);
        // The displayed formula spells out the modifier math AND already names
        // each variable it consumes (e.g. «высота потолков»). Collect those
        // variable names as a set of whole tokens so an input that the formula
        // already shows is not surfaced a SECOND time as a bare input line —
        // each parameter stays visible exactly once. We match whole tokens
        // (split on non-word/Cyrillic-aware separators), NOT raw substrings, so
        // a short or prefix name can't be dropped by an accidental partial hit;
        // and we DON'T fold the output variable (`line.out`) into the haystack.
        const formulaTokens = new Set(
          formulaLines
            .flatMap(line => line.formula.split(/[^\p{L}\p{N}_]+/u))
            .map(t => t.trim().toLowerCase())
            .filter(Boolean)
        );

        const inputs = chain.required_inputs || [];
        // Build the input-parameter lines. `базовая_сумма` is the implicit Σ of
        // pricelist positions (not a user-typed input) — it gets an explanatory
        // «сумма позиций» caption. Multi-word names (e.g. «высота потолков») are
        // checked token-by-token: skipped only when EVERY token is already in
        // the formula, so the parameter is shown there rather than duplicated.
        const inputLines = inputs
          .map(inp => {
            const name = typeof inp === 'string' ? inp : inp.name ?? '';
            const unit = typeof inp === 'string' ? '' : inp.unit ?? '';
            const type = typeof inp === 'string' ? '' : inp.type ?? '';
            const trimmed = name.trim();
            if (trimmed.toLowerCase() === 'базовая_сумма') {
              return {
                key: name,
                text: 'базовая_сумма — сумма позиций прайс-листа',
              };
            }
            // Already named in the formula (all name tokens present) → don't
            // repeat it as a bare line.
            const nameTokens = trimmed
              .split(/[^\p{L}\p{N}_]+/u)
              .map(t => t.toLowerCase())
              .filter(Boolean);
            const shownInFormula = nameTokens.length > 0 && nameTokens.every(t => formulaTokens.has(t));
            if (shownInFormula) return null;
            const suffix = `${unit ? ` (${unit})` : ''}${type ? ` — ${type}` : ''}`;
            return { key: name || trimmed, text: `${name}${suffix}` };
          })
          .filter((line): line is { key: string; text: string } => line !== null);

        return (
          <div key={chain.id} className="space-y-3">
            {/* Формула надбавки/коэффициента */}
            {formulaLines.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Формула</div>
                <div className="rounded-md border bg-gray-50 px-3 py-2 space-y-3">
                  {formulaLines.map(line => (
                    <code key={line.key} className="block text-sm text-gray-800 font-mono whitespace-pre-wrap break-words">
                      {line.out ? `${line.out} = ` : ''}
                      {line.formula}
                    </code>
                  ))}
                </div>
              </div>
            )}

            {/* Входные параметры */}
            {inputLines.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Входные параметры</div>
                <ul className="text-sm text-gray-700 space-y-0.5">
                  {inputLines.map((line, idx) => (
                    <li key={line.key || idx}>{line.text}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
